import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Loader2, RefreshCw, Save, Send, CheckCircle2, Newspaper, Mail } from "lucide-react";
import { api } from "@/lib/api";
import { ConfirmDialog } from "@/components/ConfirmDialog";

function fmt(iso) {
  if (!iso) return "";
  try { return new Date(iso).toLocaleString(undefined, { month: "short", day: "numeric", hour: "numeric", minute: "2-digit" }); } catch { return iso; }
}

export function PressReleaseReview({ slug, pageApproved, email, onChanged }) {
  const [pr, setPr] = useState(null);
  const [text, setText] = useState("");
  const [busy, setBusy] = useState(null);
  const [confirm, setConfirm] = useState(false);

  useEffect(() => {
    api.get(`/admin/winners/${slug}/press-release`).then((r) => { setPr(r.data); setText(r.data.pr_text || ""); })
      .catch(() => { setPr({ pr_status: "error" }); toast.error("Could not load the press release."); });
  }, [slug]);

  const run = async (kind, fn, ok) => {
    setBusy(kind);
    try { await fn(); if (ok) toast.success(ok); onChanged?.(); }
    catch (e) { const d = e.response?.data?.detail; toast.error(typeof d === "string" ? d : "Something went wrong."); }
    finally { setBusy(null); }
  };
  const save = () => run("save", async () => { const { data } = await api.put(`/admin/winners/${slug}/press-release`, { text }); setPr((p) => ({ ...p, pr_text: text, pr_status: data.pr_status })); }, "Press release saved.");
  const regen = () => run("regen", async () => { const { data } = await api.post(`/admin/winners/${slug}/press-release/regenerate`); setPr((p) => ({ ...p, ...data })); setText(data.pr_text); }, "New draft generated.");
  const preview = () => run("preview", async () => {
    const { data } = await api.post(`/admin/winners/${slug}/press-release/preview`, { text });
    toast.success(`Preview emailed to ${data.to}.`);
  });
  const approve = () => run("approve", async () => {
    if (text !== (pr.pr_text || "")) await api.put(`/admin/winners/${slug}/press-release`, { text });
    const { data } = await api.post(`/admin/winners/${slug}/press-release/approve`);
    setPr((p) => ({ ...p, pr_text: text, pr_status: "approved", pr_sent_at: new Date().toISOString() }));
    toast.success(`Approved and emailed to ${data.sent_to}.`);
  });

  const dirty = pr && text !== (pr.pr_text || "");
  const approved = pr?.pr_status === "approved";

  return (
    <div data-testid="pr-review" className="rounded-2xl bg-white p-4 ring-1 ring-sbg-raspberry/10">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <h3 className="inline-flex items-center gap-2 text-sm font-bold text-sbg-burgundy"><Newspaper className="h-4 w-4 text-pt-purple" /> Press release</h3>
        {pr && (
          <span data-testid="pr-status-badge" className={`rounded-full px-2.5 py-1 text-[11px] font-bold uppercase tracking-wide ${approved ? "bg-emerald-100 text-emerald-700" : "bg-amber-100 text-amber-700"}`}>
            {approved ? `Approved · emailed ${fmt(pr.pr_sent_at)}` : "Draft · awaiting approval"}
          </span>
        )}
      </div>
      {!pr ? (
        <div className="flex items-center gap-2 py-6 text-sm text-sbg-burgundy/60"><Loader2 className="h-4 w-4 animate-spin" /> Generating draft…</div>
      ) : (
        <>
          <textarea data-testid="pr-textarea" value={text} onChange={(e) => setText(e.target.value)} rows={14}
            className="mt-3 w-full rounded-xl border border-sbg-raspberry/20 bg-sbg-blush/10 p-3 font-mono text-xs leading-relaxed text-sbg-burgundy outline-none focus:border-sbg-fuchsia" />
          <p className="mt-1 text-[11px] text-sbg-burgundy/50">{approved ? "Edits to an approved release are saved but not re-emailed automatically." : `The winner cannot see this until you approve it. Approving emails it to ${email} (BCC pr@southernblackgirls.org).`}</p>
          <div className="mt-3 flex flex-wrap gap-2">
            <button data-testid="pr-save-btn" onClick={save} disabled={!dirty || busy} className="inline-flex items-center gap-1.5 rounded-full bg-sbg-burgundy/80 px-3 py-1.5 text-xs font-bold text-white disabled:opacity-50">
              {busy === "save" ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Save className="h-3.5 w-3.5" />} Save edits
            </button>
            <button data-testid="pr-regen-btn" onClick={regen} disabled={busy} className="inline-flex items-center gap-1.5 rounded-full bg-white px-3 py-1.5 text-xs font-bold text-sbg-burgundy ring-1 ring-sbg-raspberry/20 disabled:opacity-50">
              {busy === "regen" ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <RefreshCw className="h-3.5 w-3.5" />} Regenerate
            </button>
            <button data-testid="pr-preview-btn" onClick={preview} disabled={busy || !text} title="Email the current draft to yourself to see exactly what the winner will receive" className="inline-flex items-center gap-1.5 rounded-full bg-white px-3 py-1.5 text-xs font-bold text-sbg-burgundy ring-1 ring-sbg-raspberry/20 disabled:opacity-50">
              {busy === "preview" ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Mail className="h-3.5 w-3.5" />} Email me a preview
            </button>
            {approved ? (
              <button data-testid="pr-resend-btn" onClick={() => setConfirm(true)} disabled={busy} className="inline-flex items-center gap-1.5 rounded-full bg-sbg-turquoise px-3 py-1.5 text-xs font-bold text-sbg-burgundy disabled:opacity-50">
                <Send className="h-3.5 w-3.5" /> Re-send to winner
              </button>
            ) : (
              <button data-testid="pr-approve-btn" onClick={() => setConfirm(true)} disabled={busy || !pageApproved} title={pageApproved ? "" : "Approve the winner's page first"} className="inline-flex items-center gap-1.5 rounded-full bg-sbg-fuchsia px-4 py-1.5 text-xs font-bold text-white disabled:opacity-50">
                {busy === "approve" ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <CheckCircle2 className="h-3.5 w-3.5" />} Approve &amp; email to winner
              </button>
            )}
          </div>
        </>
      )}
      <ConfirmDialog open={confirm} onOpenChange={setConfirm} confirmLabel={approved ? "Re-send" : "Approve & email"} onConfirm={() => { setConfirm(false); approve(); }}
        title={approved ? "Re-send the press release?" : "Approve this press release?"}
        description={`${dirty ? "Your edits will be saved, then " : ""}the release will be emailed to ${email} with a copy to pr@southernblackgirls.org and become visible on their press release page.`} />
    </div>
  );
}
